<template>
    <div class="record-detail-files">
        <div v-if="recordFilesLoaded && recordFiles.length > 0" class="table-edit-docs">
            <div v-if="galleryFiles.length > 0 && galleryActiveIndex != null" class="collapse-body pb-0">
                <h3>Dokumentenvorschau</h3>
            </div>
            <div v-if="galleryFiles.length > 0 && galleryActiveIndex != null"
                class="preview collapse-body mb-5" style="height: 450px">
                <div v-show="galleryActiveTitle" class="galleryActiveTitle text-center" style="margin-bottom: 10px">
                    {{ galleryActiveTitle }}
                </div>
                <csp-file-gallery :active="galleryActiveIndex" :files="galleryFiles" :height="400" :width="400 / 1.414"
                    @change="onGalleryChange"/>
            </div>

            <b-table :fields="fileTableFields" :items="recordFilesForTable" :responsive="true" striped>
                <template v-slot:head()="data">
                    {{ data.label.toUpperCase() }}
                </template>
                <template v-slot:cell(actions)="data">
                    <div class="mb-3" style="text-align: right">
                        <a href="#" title="Datei ansehen" @click.prevent="galleryShow(data)">
                            <i class="inticon-manuals"/>
                        </a>
                    </div>
                    <div v-if="canSignDocument(data)" class="my-1">
                        <b-button class="btn-action w-100"
                            style="white-space: nowrap"
                            variant="primary"
                            @click.prevent="openModal(data, false)">
                            <i class="inticon-signature" style="font-size: 18px; color:#fff"/>
                            Unterschreiben
                        </b-button>
                    </div>
                    <div v-if="canCounterSignDocument(data)" class="my-1">
                        <b-button class="btn-action w-100"
                            style="white-space: nowrap"
                            variant="primary"
                            @click.prevent="openModal(data, true)">
                            <i class="inticon-signature" style="font-size: 18px; color:#fff"/>
                            Gegenzeichnen
                        </b-button>
                    </div>
                </template>
                <template v-slot:cell(dateSet)="data">
                    {{ formatDate(data.value) }}
                </template>
            </b-table>
        </div>

        <div v-if="hasNoFiles" class="collapse-body">
            <b-alert :show="true" variant="info">
                Noch keine Dateien vorhanden.
            </b-alert>
        </div>

        <b-modal v-model="isModalOpen" title="Digitale Unterschrift">
            <template v-slot:default="{ visible }">
                <div v-if="visible" class="sign-documents-modal-content">
                    <div class="collapse-body">
                        <b-form inline :novalidate="false" @submit.prevent="requestSignature">
                            <csp-form-group-input class="mr-3" :form="signForm" label-class="mr-2"
                                property="signaturePlace" required/>

                            <div class="form-group">
                                <b-button class="btn-action" :disabled="!isSignButtonEnabled"
                                    type="submit" variant="primary">
                                    <i class="inticon-signature"/>
                                    <span v-if="signForm.getProperty('isCounterSignature')">
                                        Gegenzeichnen
                                    </span>
                                    <span v-if="!signForm.getProperty('isCounterSignature')">
                                        Unterschreiben
                                    </span>
                                    <span v-if="isSignButtonLoading"
                                        aria-hidden="true" class="spinner-border spinner-border-sm" role="status"/>
                                </b-button>
                            </div>
                        </b-form>
                    </div>
                </div>
            </template>

            <template v-slot:modal-footer="{ ok }">
                <b-button variant="tertiary" @click="ok">
                    Schließen
                </b-button>
            </template>
        </b-modal>

        <csp-sign-document v-if="signatureCollection" ref="signDocument"
            :build-file-download-url="buildFileDownloadUrl"
            :record-id="recordId" :signature-collection="signatureCollection"
            @abort="handleSignAbort" @sign="handleSign"/>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import SignRepository from '@repositories/damage/SignRepository';
    import {formatDate} from '@js/functions/date';
    import FileRepository from '@repositories/file/FileRepository';
    import FileGallery from '@components/file/Gallery';
    import Form from '@form/Form';

    const container = Container.getInstance();
    const eventBus = container.getEventBus();
    const recordRepository = container.getRecordRepository();
    const signRepository = container.getSignRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        components: {
            'csp-file-gallery': FileGallery,
        },
        props: {
            recordId: {
                type: Number,
                required: true,
            },
        },
        data() {
            const signFormData = {
                signaturePlace: '',
                useRemoteAccess: false,
            }
            const signForm = new Form(signFormData);
            return {
                fileTableFields: [
                    {
                        key: 'fileCategoryGroupName',
                        label: 'Zuordnung',
                    },
                    {
                        key: 'fileCategoryName',
                        label: 'Kategorie',
                    },
                    {
                        key: 'fileName',
                        label: 'Dateiname',
                        class: 'd-none d-lg-table-cell',
                    },
                    {
                        key: 'dateSet',
                        label: 'Datum',
                    },
                    {
                        key: 'createdByName',
                        label: 'von',
                    },
                    {
                        key: 'actions',
                        label: '',
                    },
                ],
                recordFiles: [],
                recordFilesLoaded: false,
                galleryActiveIndex: null,
                galleryEnabled: false,
                galleryFiles: [],
                currentGalleryFile: null,
                signatureCollection: null,
                signForm,
                signFormData,
                isModalOpen: false,
                isSignRequestLoading: false,
                signatureFileId: null,
                wasSignaturePlaceInitialized: false,
            }
        },
        computed: {
            galleryActiveTitle() {
                if (!this.currentGalleryFile) return ''
                return this.currentGalleryFile.category + ' - ' + this.currentGalleryFile.name
            },
            hasNoFiles() {
                return this.recordFilesLoaded && this.recordFiles.length === 0;
            },
            isSignButtonEnabled() {
                return !this.isSignRequestLoading
                    && !signRepository.isSignInProgress();
            },
            isSignButtonLoading() {
                return this.isSignRequestLoading
                    || this.$root.signState.signInProgress;
            },
            recordFilesForTable() {
                const filesForTable = [];
                this.recordFiles.forEach((data) => {
                    filesForTable.push({fileId: data.id, ...data.attributes, ...data.meta});
                });
                return filesForTable;
            },
        },
        mounted() {
            this.loadFiles();

            eventBus.on(SignRepository.EVENT_SIGN_SUCCESS, this.loadFiles)
            eventBus.on(FileRepository.EVENT_UPLOAD_SUCCESS, this.loadFiles)
        },
        beforeUnmount() {
            eventBus.off(SignRepository.EVENT_SIGN_SUCCESS, this.loadFiles);
            eventBus.off(SignRepository.EVENT_UPLOAD_SUCCESS, this.loadFiles);
        },
        methods: {
            buildFileDownloadUrl(document) {
                return recordRepository.buildFileDownloadUrl(
                    this.recordId,
                    document.attributes.previewFileId
                );
            },
            canSignDocument(rowData) {
                return rowData.item.canSign;
            },
            canCounterSignDocument(rowData) {
                return rowData.item.canCounterSign;
            },
            galleryShow(rowData) {
                if (this.galleryEnabled) {
                    this.galleryActiveIndex = this.galleryFiles.findIndex(a => a.id == rowData.item.fileId)
                }
                else {
                    const url = recordRepository.buildFileDownloadUrl(rowData.item.recordId, rowData.item.fileId)
                    window.open(url, '_blank')
                }
            },
            onGalleryChange(file) {
                this.currentGalleryFile = file
            },
            formatDate,
            handleSign(signData, filledDocuments, additionalData) {
                signRepository.sign(
                    this.recordId,
                    this.signatureCollection.id,
                    signData,
                    filledDocuments,
                    additionalData
                );

                flashMessenger.addSuccess('Das Dokument wird unterschrieben...');
                this.signatureCollection = null;
            },
            handleSignAbort() {
                signRepository.signAbort(this.recordId, this.signatureCollection.id)
                    .then(
                        () => {
                            this.signatureCollection = null;
                            flashMessenger.addInfo('Der Vorgang wurde abgebrochen.');
                            this.loadFiles();
                        },
                        () => {
                            this.signatureCollection = null;
                            flashMessenger.addError(
                                'Beim Abbruch ist ein Fehler aufgetreten.' +
                                    ' Löschen Sie überflüssige Dateien ggf. manuell.'
                            );
                        }
                    );
            },
            loadGalleryFiles() {
                return recordRepository.getThumbnailData(this.recordId, 'carousel').then(
                    (data) => {
                        const galleryThumbnailUrls = {}
                        data.data.forEach(fileThumbnail => {
                            galleryThumbnailUrls[fileThumbnail.attributes.fileId] =
                                recordRepository.buildThumbnailDownloadUrl(
                                    this.recordId, fileThumbnail.attributes.fileId, 'carousel')
                        });
                        this.galleryFiles = this.recordFiles.map(recordFile => {
                            return {
                                category: recordFile.attributes.fileCategoryName,
                                id: recordFile.id,
                                name: recordFile.attributes.fileName,
                                thumbnailUrl: galleryThumbnailUrls[recordFile.id],
                                url: recordRepository.buildFileDownloadUrl(this.recordId, recordFile.id),
                            }
                        })
                    }
                );
            },
            loadFiles() {
                return recordRepository.getFileData(this.recordId).then(
                    (data) => {
                        this.recordFiles = data.data.attributes.files.data;
                        this.setSignaturePlace(data.data.meta.signaturePlace);
                        if (this.galleryEnabled) {
                            this.loadGalleryFiles()
                        }
                        this.recordFilesLoaded = true;
                    }
                );
            },
            openModal(rowData, isCounterSignature) {
                this.signatureFileId = parseInt(rowData.item.fileId);
                this.signForm.setProperty('isCounterSignature', isCounterSignature);
                this.signForm.clearValidationErrors();
                this.isModalOpen = true;
            },
            requestSignature() {
                if (!this.isSignButtonEnabled) {
                    return;
                }
                this.isSignRequestLoading = true;
                signRepository.recordFileSignRequest(this.recordId, this.signatureFileId, this.signForm)
                    .then(
                        (response) => {
                            this.signatureCollection = response.data;
                            this.isSignRequestLoading = false;
                            this.isModalOpen = false;
                        },
                        (error) => {
                            this.signForm.setValidationErrors(error);
                            this.isSignRequestLoading = false;
                        }
                    );
            },
            setSignaturePlace(signaturePlace) {
                // Make sure the signature place is only initialized once when the component
                // loads to prevent losing changes the user has made.
                if (this.wasSignaturePlaceInitialized) {
                    return;
                }
                this.wasSignaturePlaceInitialized = true;
                this.signFormData.signaturePlace = signaturePlace;
            },
        },
    }
</script>
